import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";

const DEADLINES: string[][] = [
  ["Extended European Search Report (Rule 70a)", "6 months from publication of the search report", "Optional but usually answered together with the examination request"],
  ["First examination report (Art. 94(3))", "4 months, extendable to 6 on request", "The main substantive round"],
  ["Rule 161/162 communication (PCT regional phase)", "6 months, non-extendable", "Amendment window on entering the European phase"],
  ["Summons to oral proceedings", "Usually 2 months before the hearing", "Final written submissions and auxiliary requests"],
  ["Rule 71(3) intention to grant", "4 months, non-extendable", "Claim translations and grant fee"],
];

const FAQS = [
  {
    q: "What is an EPO office action?",
    a: "A communication from the examining division under Article 94(3) EPC setting out objections to novelty, inventive step, clarity or added matter, with a deadline — normally four months, extendable to six — to file arguments and any amendments.",
  },
  {
    q: "How long do I have to respond to an EPO examination report?",
    a: "Four months from notification, extendable to six months on a simple request. The Rule 161/162 and Rule 71(3) deadlines are shorter and cannot be extended.",
  },
  {
    q: "What does an EPO office action response cost?",
    a: "Typically €1,500 to €3,500 for a standard substantive response, depending on the number of objections and whether new claim requests are needed. Fixed-fee quotes are given before work starts.",
  },
  {
    q: "Can my US attorney's arguments be filed at the EPO?",
    a: "Only through a European representative, and usually not unchanged. Unexpected-results declarations and secondary considerations carry little weight; the EPO decides inventive step under the problem-solution approach and refuses amendments without literal support in the application as filed.",
  },
  {
    q: "What happens if I miss the deadline?",
    a: "The application is deemed withdrawn. Further processing under Rule 135 EPC is available within two months of the loss-of-rights notice, on payment of a fee and completion of the omitted act.",
  },
];

export const Route = createFileRoute("/services/epo-office-action-response")({
  head: () => ({
    meta: [
      { title: "EPO Office Action Response for US Applicants | Bauer IP" },
      {
        name: "description",
        content:
          "Received an Article 94(3) examination report? A registered European Patent Attorney drafts and files the response — deadlines, strategy and fixed-fee quotes.",
      },
      {
        property: "og:title",
        content: "EPO Office Action Response for US Applicants",
      },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "Deadlines, problem-solution strategy and added-matter limits for US applicants answering an EPO examination report.",
      },
      { property: "og:url", content: "https://bauerip.com/services/epo-office-action-response" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [
      { rel: "canonical", href: "https://bauerip.com/services/epo-office-action-response" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "EPO office action response",
          name: "EPO office action response for US applicants",
          url: "https://bauerip.com/services/epo-office-action-response",
          provider: {
            "@type": "Person",
            name: "Karel Bauer",
            jobTitle: "European Patent Attorney",
          },
          areaServed: [{ "@type": "Country", name: "United States" }],
          audience: {
            "@type": "Audience",
            audienceType: "US companies and US patent attorneys",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: OfficeActionPage,
});

function OfficeActionPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        <Section
          eyebrow="EPO prosecution"
          title="EPO Office Action Response for US Applicants"
          titleAs="h1"
          intro="An Article 94(3) communication is where most US-drafted applications lose scope in Europe. I draft and file the response as your European representative — arguments in the EPO's own framework, amendments that survive the added-matter test, and a fixed fee agreed before work starts."
        >
          <div className="flex flex-wrap gap-3">
            <CTAButton
              variant="accent"
              trackLabel="office_action_consultation"
              href={mailto({
                subject: "EPO office action response",
                body: "Hello Karel,\n\nI have an EPO communication to answer.\n\n— Application number:\n— Date of the communication:\n— Response deadline:\n— Objections raised (Art. 54 / 56 / 84 / 123(2)):\n",
              })}
            >
              Send me the office action
            </CTAButton>
            <Link
              to="/services/european-patent-attorney"
              className="inline-flex items-center justify-center rounded-full border border-foreground/25 px-6 py-3 text-sm font-medium tracking-wide text-foreground transition-colors hover:border-foreground hover:bg-foreground/5"
            >
              Full European patent services →
            </Link>
          </div>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="Deadlines"
          title="Which EPO communication are you holding?"
        >
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">EPO communications and their response deadlines</caption>
              <thead className="bg-secondary/50 text-foreground">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">Communication</th>
                  <th scope="col" className="px-4 py-3 font-medium">Deadline</th>
                  <th scope="col" className="px-4 py-3 font-medium">What it decides</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {DEADLINES.map((row) => (
                  <tr key={row[0]} className="border-t border-border">
                    {row.map((cell, i) => (
                      <td key={i} className={`px-4 py-3 ${i === 0 ? "text-foreground" : ""}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="Why US arguments need translating"
          title="What the EPO actually wants to read"
        >
          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                t: "Problem-solution, not KSR",
                d: "Inventive step is decided from the closest prior art and an objective technical problem. Commercial success and long-felt need carry almost no weight.",
              },
              {
                t: "Added matter is unforgiving",
                d: "Article 123(2) requires direct and unambiguous support in the application as filed. Intermediate generalisations that pass at the USPTO are refused here.",
              },
              {
                t: "Technical effect wins CII cases",
                d: "For software claims, the argument must identify a technical effect — latency, memory, signal quality, control of a process — not a business advantage.",
              },
            ].map((item) => (
              <div key={item.t}>
                <h2 className="font-serif text-xl text-foreground">{item.t}</h2>
                <p className="mt-3 text-muted-foreground leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-border bg-secondary/30 p-6 md:p-8">
            <h2 className="font-serif text-xl text-foreground">How the engagement runs</h2>
            <ol className="mt-5 space-y-4 text-sm text-muted-foreground">
              {[
                "You send the communication, the application as filed and any parallel USPTO prosecution history.",
                "Within two business days you get a written view of the objections, the realistic claim scope and a fixed fee.",
                "I draft the response with a main request and auxiliary requests, and send it for your approval.",
                "I file at the EPO, confirm the receipt to your docket and diarise the next deadline.",
              ].map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-serif text-accent-text">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </Section>

        <Section
          className="border-t border-border"
          eyebrow="FAQ"
          title="EPO office action — common questions"
        >
          <dl className="grid gap-8 md:grid-cols-2">
            {FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-serif text-xl text-foreground">{f.q}</dt>
                <dd className="mt-3 text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-12 text-sm text-muted-foreground">
            Related:{" "}
            <Link
              to="/blog/$slug"
              params={{ slug: "epo-vs-uspto-patent-practice-guide" }}
              className="underline underline-offset-4"
            >
              EPO vs USPTO practice for US filers
            </Link>{" "}
            ·{" "}
            <Link
              to="/services/foreign-associate-for-us-firms"
              className="underline underline-offset-4"
            >
              Foreign associate services for US firms
            </Link>
          </p>
        </Section>
      </main>

      <SiteFooter />
    </div>
  );
}
